import {CommonModule} from '@angular/common';
import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren} from '@angular/core';

import {TranslateModule} from '@ngx-translate/core';
import * as moment from 'moment';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {EMPTY, Observable} from 'rxjs';
import {catchError, filter, finalize, switchMap, take, takeUntil, tap} from 'rxjs/operators';

import {EmployeeVariablesService} from '../../+payrolls/prepare/variable-elements/employee-variables.service';
import {Destroyable} from '../../destroyable.component';
import {PayPeriodAttributes} from '../../models/models.interfaces';
import {ApiListData, Data} from '../api/server-responses.interfaces';

@Component({
  selector: 'gp-pay-period-selector-bar',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
  templateUrl: './pay-period-selector-bar.component.html',
  styleUrls: ['./pay-period-selector-bar.component.scss']
})
export class PayPeriodSelectorBarComponent extends Destroyable implements OnChanges {

  @Input()
  public contractId: string | number;

  @Input()
  public contractBeginDate: string;

  @Input()
  public contractEndDate: string;

  @Input()
  public payPeriodId: number;

  @Input()
  public beforeSelect: () => Observable<boolean>;

  @Output()
  public payPeriodChange = new EventEmitter<Data<PayPeriodAttributes>>();

  @ViewChild('payPeriodList')
  public payPeriodList: ElementRef<HTMLElement>;

  @ViewChildren('periodButton')
  public periodButtons: QueryList<ElementRef<HTMLElement>>;

  public payPeriods: Data<PayPeriodAttributes>[] = [];
  public selectedPayPeriod: Data<PayPeriodAttributes>;
  private previousPayPeriod: Data<PayPeriodAttributes>;
  private nextPayPeriod: Data<PayPeriodAttributes>;

  constructor(private employeeVariablesService: EmployeeVariablesService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public get canNavigateBack(): boolean {
    return !!this.previousPayPeriod?.id;
  }

  public get canNavigateForward(): boolean {
    return !!this.nextPayPeriod?.id;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['contractId'] || changes['contractBeginDate'] || changes['contractEndDate']) {
      this.reset();
      if (this.contractId) {
        this.loadPayPeriods(this.payPeriodId);
      }
    } else if (changes['payPeriodId'] && this.payPeriodId && this.payPeriodId !== this.selectedPayPeriod?.id) {
      this.loadPayPeriods(this.payPeriodId);
    }
  }

  public navigatePrevious(): void {
    if (this.canNavigateBack) {
      this.select(this.previousPayPeriod.id);
    }
  }

  public navigateNext(): void {
    if (this.canNavigateForward) {
      this.select(this.nextPayPeriod.id);
    }
  }

  public select(payPeriodId: number): void {
    if (!this.beforeSelect) {
      this.loadPayPeriods(payPeriodId);
      return;
    }
    this.beforeSelect()
      .pipe(filter(Boolean), take(1), takeUntil(this.componentDestroyed$))
      .subscribe(() => this.loadPayPeriods(payPeriodId));
  }

  public onWheelScroll(event: WheelEvent): void {
    this.payPeriodList.nativeElement.scrollLeft += event.deltaY > 0 ? 50 : -50;
    event.preventDefault();
  }

  private checkPeriodLimit(): void {
    if (this.contractBeginDate && this.selectedPayPeriod
      && moment(this.contractBeginDate).isSameOrAfter(this.selectedPayPeriod.attributes.begindate, 'month')) {
      this.previousPayPeriod = null;
    }
  }

  private reset(): void {
    this.payPeriods = [];
    this.selectedPayPeriod = undefined;
    this.previousPayPeriod = undefined;
    this.nextPayPeriod = undefined;
  }

  private loadPayPeriods(payPeriodId?: number): void {
    this.employeeVariablesService.getContractPayPeriodRange(+this.contractId, payPeriodId)
      .pipe(
        tap((range: ApiListData<PayPeriodAttributes>) => this.setRange(range?.data || [], payPeriodId)),
        switchMap(() => this.selectedPayPeriod && !this.payPeriods.length
          ? this.employeeVariablesService.getPayPeriodsBySeriesId(
            this.selectedPayPeriod.attributes.serie.id, this.contractBeginDate, this.contractEndDate)
          : EMPTY),
        tap((payPeriods) => this.payPeriods = payPeriods.data),
        catchError(() => EMPTY),
        finalize(() => {
          this.cd.detectChanges();
          this.scrollToSelectedPeriod();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private setRange(range: Data<PayPeriodAttributes>[], payPeriodId?: number): void {
    const selected = payPeriodId
      ? range.find(payPeriod => payPeriod.id === payPeriodId)
      : range.length === 3 ? range[1] : (range[0]?.attributes.status === 'ONGOING' ? range[0] : range[range.length - 1]);
    const index = range.indexOf(selected);
    this.selectedPayPeriod = selected;
    this.previousPayPeriod = index > 0 ? range[index - 1] : undefined;
    this.nextPayPeriod = index >= 0 ? range[index + 1] : undefined;
    this.checkPeriodLimit();
    this.payPeriodChange.emit(selected);
  }

  private scrollToSelectedPeriod(): void {
    setTimeout(() => {
      const index = this.payPeriods.findIndex(payPeriod => payPeriod.id === this.selectedPayPeriod?.id);
      this.periodButtons?.get(index)?.nativeElement
        .scrollIntoView({inline: 'center', behavior: 'smooth', block: 'nearest'});
    }, 100);
  }
}
